/**
 * iter-371 — Public BOQ OTP Gate overlay
 *
 * Shown before the Public BOQ contents when the admin has enabled the
 * "OTP Gate" on this share link. Two-step flow:
 *   1. Enter your email → POST /public/boq/{token}/request-otp
 *   2. Enter the 6-digit code → POST /public/boq/{token}/verify-otp
 *      Response { session_token } is stored in sessionStorage and passed
 *      as `X-BOQ-Session` on every subsequent GET.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Loader2, ShieldCheck, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicBOQOTPGate({ token, gateMessage, onUnlocked }) {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const requestOtp = async () => {
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error('Enter a valid email');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/boq/${token}/request-otp`, { email: email.trim() });
      toast.success('OTP sent to your email');
      setStep('otp');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    if (!otp.trim() || otp.trim().length < 4) { toast.error('Enter the code from your email'); return; }
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/public/boq/${token}/verify-otp`, {
        email: email.trim(),
        otp: otp.trim(),
      });
      if (r.data?.session_token) {
        // iter-371-hotfix2 — use localStorage so the verified session
        // survives tab close, refresh, and new-tab opens of the same link
        // (30-day server-side TTL matches this).
        try { localStorage.setItem(`boq_otp_${token}`, r.data.session_token); } catch { /* silent */ }
        toast.success('Access unlocked');
        onUnlocked?.(r.data.session_token);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-amber-100 flex items-center justify-center px-4" data-testid="public-boq-otp-gate">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-amber-100 overflow-hidden">
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-6 py-5 text-white">
          <div className="flex items-center gap-2">
            <ShieldCheck size={20} />
            <h2 className="font-bold text-lg">Secure Quotation Access</h2>
          </div>
          <p className="text-amber-100 text-sm mt-1">
            {gateMessage || "Enter the email address this quotation was shared with. We'll send you a one-time code."}
          </p>
        </div>

        <div className="p-6 space-y-4">
          {step === 'email' && (
            <>
              <div>
                <label className="text-sm font-medium text-stone-700 flex items-center gap-1 mb-1">
                  <Mail size={14} /> Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') requestOtp(); }}
                  placeholder="you@example.com"
                  autoFocus
                  data-testid="otp-gate-email"
                />
              </div>
              <Button
                onClick={requestOtp}
                disabled={sending}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="otp-gate-send-btn"
              >
                {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {sending ? 'Sending code…' : 'Send me the code'}
              </Button>
            </>
          )}

          {step === 'otp' && (
            <>
              <p className="text-sm text-stone-600">
                We sent a 6-digit code to <strong className="text-stone-900">{email}</strong>. Check your inbox (and spam folder).
              </p>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1 block">One-time code</label>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
                  placeholder="123456"
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  autoFocus
                  data-testid="otp-gate-otp-input"
                />
              </div>
              <Button
                onClick={verify}
                disabled={verifying || otp.length < 4}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="otp-gate-verify-btn"
              >
                {verifying ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                {verifying ? 'Verifying…' : 'Unlock quotation'}
              </Button>
              <button
                onClick={() => { setStep('email'); setOtp(''); }}
                className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mx-auto block"
                data-testid="otp-gate-restart-btn"
              >
                <RotateCcw size={11} /> Use a different email
              </button>
            </>
          )}
          <p className="text-[10px] text-center text-stone-400 pt-2">
            Codes expire in 10 minutes. Access is remembered on this device for 30 days.
          </p>
        </div>
      </div>
    </div>
  );
}
